import type { DisplayMode } from "../store/ModifiersStoreContext";

import { useCallback } from "react";

import {
  useIsIframeLoaded,
  useModifiersContext,
} from "../store/ModifiersStoreContext";

type IframeMessage =
  | {
      type: "APPLY_PREVIEW";
      payload: {
        selector: string;
        newHtml?: string;
        containerOpeningTag?: string;
      };
    }
  | { type: "CLEAR_SELECTION"; payload?: Record<string, never> }
  | {
      type: "UPDATE_TEXT_PREVIEW";
      payload: { selector: string; text: string };
    }
  | { type: "SET_DISPLAY_MODE"; payload: { displayMode: DisplayMode } };

export function useIframeMessaging() {
  const { store } = useModifiersContext();
  const isIframeLoaded = useIsIframeLoaded();

  const sendMessage = useCallback(
    (message: IframeMessage) => {
      // NOTE (Nick, 2025-12-04): Don't send messages until iframe is ready to prevent race conditions
      const currentIsLoaded = store.getState().isIframeLoaded;
      if (!currentIsLoaded) {
        return;
      }

      // NOTE (Kurt, 2025-11-24): Always read current container from store instead of closing
      // over it, so we get the latest value even when called from async contexts like setTimeout.
      const container = store.getState().iframeContainer;

      if (!container) {
        return;
      }

      const iframe = container.querySelector("iframe");

      // NOTE (Yuxin, 2026-02-03): Don't throw - just return early like other guards
      // The iframe may not be mounted yet during initial render
      if (!iframe?.contentWindow) {
        return;
      }

      iframe.contentWindow.postMessage(message, "*");
    },
    [store],
  );

  return { sendMessage, isIframeLoaded };
}
